import { Route } from '@angular/router';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { LayoutComponent } from './shared/components/layout/layout.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { WorkersComponent } from './pages/workers/workers.component';
import { FirmwaresComponent } from './pages/firmwares/firmwares.component';
import { MaintenanceComponent } from './pages/maintenance/maintenance.component';
import { ScannerComponent } from './pages/scanner/scanner.component';
import { HistoryComponent } from './pages/history/history.component';
import { UsersComponent } from './pages/users/users.component';
import { RolesComponent } from './pages/roles/roles.component';
import { authGuard } from './core/guards/auth.guard';
import { AgentsComponent } from './pages/agents/agents.component';
import { WorkerSources } from './core/openapi/bff-admin-api';
import { ServiceCentersComponent } from './pages/service-centers/service-centers.component';
import { ScannerDetailsComponent } from './pages/scanner/scanner-details/scanner-details.component';
import { DEVICE_ID_PARAM } from './pages/devices/devices.routes';
import { DEVICE_CARD_CHILDREN } from './pages/devices/views/device-list/components/device-card/device-card.routes';
import { permissionGuard } from './core/guards/permission.guard';
import { UserPermissionEnum } from './core/models/user-permissions.enum';
import { registeredEquipmentCardGuard } from './core/guards/registered-equipment-card.guard';

export const appRoutes: Route[] = [
  {
    path: '',
    component: LayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    // resolve: [userResolver],
    children: [
      {
        path: '',
        component: DashboardComponent,
        data: {
          titleTranslateKey: 'monitoring.menu.dashboard',
        },
      },
      {
        path: 'facilities',
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('./pages/facilities/facilities.routes').then((m) => m.FACILITIES_ROUTES),
        data: {
          titleTranslateKey: 'monitoring.menu.facilities',
          requiredPermission: UserPermissionEnum.FacilityRead,
        },
      },
      {
        path: 'placement',
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('./pages/placement/placement.routes').then((m) => m.PLACEMENT_ROUTES),
        data: {
          titleTranslateKey: 'monitoring.menu.placement',
          requiredPermission: UserPermissionEnum.ContainerRead,
        },
      },
      {
        path: 'workers/manual',
        canActivate: [permissionGuard],
        component: WorkersComponent,
        data: {
          titleTranslateKey: 'monitoring.menu.workers',
          workerSource: WorkerSources.Manual,
          requiredPermission: UserPermissionEnum.WorkerRead,
        },
      },
      {
        path: 'workers',
        canActivate: [permissionGuard],
        component: WorkersComponent,
        data: {
          titleTranslateKey: 'monitoring.menu.workers',
          workerSource: WorkerSources.Billing,
          requiredPermission: UserPermissionEnum.WorkerRead,
        },
      },
      {
        path: 'devices',
        canActivate: [permissionGuard],
        loadChildren: () => import('./pages/devices/devices.routes').then((m) => m.DEVICES_ROUTES),
        data: {
          titleTranslateKey: 'monitoring.menu.devices',
          requiredPermission: UserPermissionEnum.EquipmentRead,
        },
      },
      {
        path: 'firmwares',
        canActivate: [permissionGuard],
        component: FirmwaresComponent,
        data: {
          titleTranslateKey: 'monitoring.menu.firmwares',
          requiredPermission: UserPermissionEnum.FirmwareRead,
        },
      },
      {
        path: 'maintenance',
        canActivate: [permissionGuard],
        component: MaintenanceComponent,
        data: {
          titleTranslateKey: 'monitoring.menu.maintenance',
          requiredPermission: UserPermissionEnum.MaintenanceRead,
        },
        children: [
          {
            path: `:${DEVICE_ID_PARAM}`,
            canActivate: [registeredEquipmentCardGuard],
            loadComponent: () =>
              import(
                './pages/devices/views/device-list/components/device-card/device-card.component'
              ).then((m) => m.DeviceCardComponent),
            children: DEVICE_CARD_CHILDREN,
          },
        ],
      },
      {
        path: 'scanner/:scanId',
        canActivate: [permissionGuard],
        component: ScannerDetailsComponent,
        data: {
          titleTranslateKey: 'monitoring.scanner.details.title',
          requiredPermission: UserPermissionEnum.ScansRead,
        },
      },
      {
        path: 'scanner',
        canActivate: [permissionGuard],
        component: ScannerComponent,
        data: {
          titleTranslateKey: 'monitoring.menu.scanner',
          requiredPermission: UserPermissionEnum.ScansRead,
        },
      },
      {
        path: 'agents',
        canActivate: [permissionGuard],
        component: AgentsComponent,
        data: {
          titleTranslateKey: 'monitoring.menu.agents',
          requiredPermission: UserPermissionEnum.AgentRead,
        },
      },
      {
        path: 'notifications',
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('./pages/notifications/notifications.routes').then((m) => m.NOTIFICATIONS_ROUTES),
        data: {
          titleTranslateKey: 'monitoring.menu.notifications',
          requiredPermission: UserPermissionEnum.EquipmentEventRead,
        },
      },
      {
        path: 'history',
        canActivate: [permissionGuard],
        component: HistoryComponent,
        data: {
          titleTranslateKey: 'monitoring.menu.history',
          requiredPermission: UserPermissionEnum.AuditRead,
        },
      },
      {
        path: 'service-centers',
        canActivate: [permissionGuard],
        component: ServiceCentersComponent,
        data: {
          titleTranslateKey: 'monitoring.menu.service-centers',
          requiredPermission: UserPermissionEnum.MaintenanceRead,
        },
      },
      {
        path: 'knowledge-base',
        loadChildren: () =>
          import('./pages/knowledge-base/knowledge-base.routes').then(
            (m) => m.KNOWLEDGE_BASE_ROUTES,
          ),
        data: {
          titleTranslateKey: 'monitoring.menu.knowledge-base',
        },
      },
      {
        path: 'users',
        canActivate: [permissionGuard],
        component: UsersComponent,
        data: {
          titleTranslateKey: 'monitoring.menu.users',
          requiredPermission: UserPermissionEnum.UserRead,
        },
      },
      {
        path: 'roles',
        canActivate: [permissionGuard],
        component: RolesComponent,
        data: {
          titleTranslateKey: 'monitoring.menu.roles',
          requiredPermission: UserPermissionEnum.RoleRead,
        },
      },
      {
        path: '404',
        data: {
          titleTranslateKey: 'monitoring.menu.404',
        },
        component: NotFoundComponent,
      },
      {
        path: 'no-permission',
        data: {
          titleTranslateKey: 'monitoring.permissions.no-view-permission-title',
        },
        loadComponent: () =>
          import('./shared/components/no-permission/no-permission.component').then(
            (m) => m.NoPermissionComponent,
          ),
      },
    ],
  },
  {
    path: 'login',
    loadChildren: () => import('./auth/auth.routes').then((m) => m.AUTH_ROUTES),
    data: {
      titleTranslateKey: 'monitoring.login',
    },
  },
  {
    path: '**',
    redirectTo: '404',
    pathMatch: 'full',
  },
];
